import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { IconActionDropDown } from 'wg-fe-ui';
import { VersionsProps } from './VehicleLookupOrderTable';

interface Props {
  sortVersions: (p: string, d: VersionsProps[]) => void;
  sortedBy?: {
    [k: string]: string;
  };
  versions: VersionsProps[];
}

interface ClickAreaProps {
  isActive: boolean;
  desc: boolean;
  clickable: boolean | VersionsProps[];
}

const VehicleLookupOrderTableHead: React.FC<React.PropsWithChildren<Props>> = ({
  sortVersions,
  sortedBy,
  versions,
}) => {
  const { t } = useTranslation();
  return (
    <VersionLabel>
      <Version onClick={() => sortVersions('version', versions)}>
        <ClickableArea
          isActive={sortedBy?.key === 'version'}
          desc={sortedBy?.type === 'desc'}
          clickable={versions?.length !== 0 && versions}
        >
          <VersionTitle>{t('Vehicles.Version')}</VersionTitle>
          <StyledIconActionDropDown color="#8990a3" />
        </ClickableArea>
      </Version>
      <Kw onClick={() => sortVersions('kw', versions)}>
        <ClickableArea
          isActive={sortedBy?.key === 'kw'}
          desc={sortedBy?.type === 'desc'}
          clickable={versions?.length !== 0 && versions}
        >
          {t('kW')}
          <StyledIconActionDropDown color="#8990a3" />
        </ClickableArea>
      </Kw>
      <CC onClick={() => sortVersions('cc', versions)}>
        <ClickableArea
          isActive={sortedBy?.key === 'cc'}
          desc={sortedBy?.type === 'desc'}
          clickable
        >
          {t('Vehicles.CC')}
          <StyledIconActionDropDown color="#8990a3" />
        </ClickableArea>
      </CC>
      <CoTwo onClick={() => sortVersions('co2', versions)}>
        <ClickableArea
          isActive={sortedBy?.key === 'co2'}
          desc={sortedBy?.type === 'desc'}
          clickable
        >
          CO<Sub>2</Sub>
          <StyledIconActionDropDown color="#8990a3" />
        </ClickableArea>
      </CoTwo>
      <ModelYear onClick={() => sortVersions('modelYear', versions)}>
        <ClickableArea
          isActive={sortedBy?.key === 'modelYear'}
          desc={sortedBy?.type === 'desc'}
          clickable={versions?.length !== 0 && versions}
        >
          {t('Year')}
          <StyledIconActionDropDown color="#8990a3" />
        </ClickableArea>
      </ModelYear>
      <Price onClick={() => sortVersions('price', versions)}>
        <ClickableArea
          isActive={sortedBy?.key === 'price'}
          desc={sortedBy?.type === 'desc'}
          clickable={versions?.length !== 0 && versions}
        >
          {t('Catalogue value')}
          <StyledIconActionDropDown color="#8990a3" />
        </ClickableArea>
      </Price>
    </VersionLabel>
  );
};

const VersionLabel = styled.div`
  font-size: 1.6rem;
  line-height: 1.6;
  width: 100%;

  background-color: #f9f9fa;
  border-bottom: 1px solid;
  border-top: 1px solid;
  border-color: ${({ theme }) => theme.gray[100]};
  color: ${({ theme }) => theme.labels.guaranteeText};
  font-weight: 500;

  padding: 1.6rem calc(1rem - 4px) 1.6rem 3rem;
`;

const VersionLabelItem = styled.div`
  display: inline-flex;
  align-items: center;
`;

const Version = styled(VersionLabelItem)`
  width: 40%;
  padding-right: 2rem;
`;

const Kw = styled(VersionLabelItem)`
  width: 11%;
  padding-right: 1rem;
`;

const ModelYear = styled(VersionLabelItem)`
  width: 11%;
  padding-right: 1rem;
`;

const CC = styled(VersionLabelItem)`
  width: 11%;
  padding-right: 1rem;
`;

const CoTwo = styled(VersionLabelItem)`
  width: 11%;
  padding-right: 1rem;
`;

const Price = styled(VersionLabelItem)`
  justify-content: flex-end;
  text-align: right;
  width: 16%;
`;

const ClickableArea = styled.div<ClickAreaProps>`
  display: inline-flex;
  align-items: center;

  svg {
    margin-top: 2px;
    opacity: 0;
  }

  ${({ clickable }) =>
    clickable &&
    css`
      cursor: pointer;

      transition: color 0.2s, stroke 0.2s, opacity 0.2s;
      &:hover {
        color: #626978;

        svg {
          opacity: 1;
        }

        svg > path {
          stroke: #626978;
        }
      }
    `}

  ${({ clickable, isActive }) =>
    clickable &&
    isActive &&
    css`
      font-weight: 500;

      svg {
        opacity: 1;
      }
    `}

  ${({ clickable, isActive, desc }) =>
    clickable &&
    isActive &&
    desc &&
    css`
      svg {
        transform: rotate(180deg);
      }
    `}
`;

const VersionTitle = styled.p`
  display: inline-block;
  margin: 0;
  padding-right: 1rem;
`;

const StyledIconActionDropDown = styled(IconActionDropDown)`
  height: 2rem;
  width: 25;
`;

const Sub = styled.sub`
  margin-top: 1rem;
  vertical-align: sub;
  font-size: xx-small;
`;

export default VehicleLookupOrderTableHead;
